/**
 * Reviewing bug reports (/bugs/review), for admins: what waits, with Publish and Reject; what
 * is published, with Mark fixed, Reopen and Unpublish; what was rejected, with Publish. A note
 * goes out with the decision and shows beside the report ("Fixed in the 14 October build").
 * The server decides who is an admin (an address listed in `admins`).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, ShieldCheck } from 'lucide-react';
import { BetaTag, BrandMark } from '../components/BrandMark';
import { usePageMeta } from '../hooks/usePageMeta';
import { agoLabel } from '../utils/ideas';
import { MAX_NOTE, areaLabel, type BugReport, type ReviewAction } from '../utils/bugReports';
import { bugErrorText, reviewBug, reviewList } from '../utils/bugClient';

const GROUPS: { status: BugReport['status']; title: string; actions: { action: ReviewAction; label: string; kind: string }[] }[] = [
  { status: 'pending', title: 'Waiting for review', actions: [{ action: 'publish', label: 'Publish', kind: 'ns-btn-primary' }, { action: 'reject', label: 'Reject', kind: 'ns-btn-ghost' }] },
  { status: 'published', title: 'Published', actions: [{ action: 'fix', label: 'Mark fixed', kind: 'ns-btn-primary' }, { action: 'reopen', label: 'Reopen', kind: 'ns-btn-ghost' }, { action: 'unpublish', label: 'Unpublish', kind: 'ns-btn-ghost' }] },
  { status: 'rejected', title: 'Rejected', actions: [{ action: 'publish', label: 'Publish after all', kind: 'ns-btn-ghost' }] },
];

export const BugReviewPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  usePageMeta({ title: 'Review bug reports — Noodle Studio', description: 'Admins only.', canonical: 'https://noodlestudio.ai/bugs/review' });
  const [reports, setReports] = useState<BugReport[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const now = Date.now();

  const load = useCallback(async () => {
    try {
      setReports(await reviewList());
      setProblem(null);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      if (status === 403 || status === 401) setForbidden(true);
      else setProblem(bugErrorText(e, 'The reports could not be loaded.'));
      setReports([]);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const decide = async (report: BugReport, action: ReviewAction) => {
    setBusy(report.id);
    setProblem(null);
    try {
      const { report: next } = await reviewBug(report.id, action, notes[report.id] ?? report.note ?? '');
      setReports(current => (current ?? []).map(r => (r.id === next.id ? next : r)));
    } catch (e) {
      setProblem(bugErrorText(e, 'The decision could not be saved.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-cream text-ink" data-bug-review-page>
      <header className="ns-pagebar">
        <div className="w-full max-w-4xl mx-auto flex items-center justify-between gap-3">
          <button onClick={onBack} className="ns-btn ns-btn-ghost ns-btn-sm shrink-0">
            <ArrowLeft size={14} aria-hidden="true" />
            Known issues
          </button>
          <Link to="/" className="flex items-center gap-2.5 rounded-full shrink-0" aria-label="Noodle Studio home">
            <BrandMark size={32} />
            <span className="text-[1.0625rem] font-bold tracking-[-0.02em] text-ink">Noodle Studio</span>
            <BetaTag />
          </Link>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-8">
        <div className="max-w-4xl mx-auto flex flex-col gap-8">
          <div>
            <p className="text-2xs font-bold tracking-[0.14em] uppercase text-ink-soft flex items-center gap-1.5"><ShieldCheck size={12} aria-hidden="true" />Admins</p>
            <h1 className="font-brand font-normal text-[2.5rem] sm:text-[3rem] leading-[1.04] tracking-[-0.01em] mt-2">Review the reports</h1>
            <p className="mt-3 text-[1.0625rem] leading-[1.55] text-ink-soft max-w-[34rem]">Publish what is real, reject what is not, and say what was done. The note goes out with the report.</p>
          </div>

          {forbidden && <p className="ns-note" data-tone="warn" role="alert" data-bug-review-forbidden>Admins only. Your address is not on the admins list.</p>}
          {problem && <p className="ns-note" data-tone="danger" role="alert">{problem}</p>}
          {reports === null && <p className="ns-t-meta flex items-center gap-2"><Loader2 size={14} className="animate-spin" aria-hidden="true" />Loading</p>}

          {reports !== null && !forbidden && GROUPS.map(group => {
            const list = reports.filter(r => r.status === group.status);
            return (
              <section key={group.status} aria-labelledby={`review-${group.status}`} className="flex flex-col gap-3" data-bug-review-group={group.status}>
                <h2 id={`review-${group.status}`} className="text-2xs font-bold tracking-[0.14em] uppercase text-ink-soft">{group.title} · {list.length}</h2>
                {list.length === 0 && <p className="ns-tile text-[0.9375rem] text-ink-soft">Nothing here.</p>}
                {list.map(report => (
                  <article key={report.id} className="ns-tile flex flex-col gap-3" data-bug-report={report.id}>
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-[1rem] font-bold leading-snug">{report.title}</h3>
                      {report.fixed && <span className="ns-chip ns-chip-sm bg-mint-soft text-mint-deep shrink-0"><Check size={12} aria-hidden="true" />Fixed</span>}
                    </div>
                    <p className="text-[0.9375rem] leading-[1.55] whitespace-pre-wrap">{report.details}</p>
                    <p className="ns-t-meta flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="ns-chip ns-chip-sm bg-cream-200 text-ink-soft">{areaLabel(report.area)}</span>
                      <span>{report.reporterName} · {agoLabel(report.createdAt, now)}{report.reviewedAt ? ` · reviewed ${agoLabel(report.reviewedAt, now)}` : ''}</span>
                    </p>
                    <label className="flex flex-col gap-1.5">
                      <span className="ns-t-label">A word on it, shown with the report</span>
                      <input type="text" value={notes[report.id] ?? report.note ?? ''} onChange={e => setNotes(current => ({ ...current, [report.id]: e.target.value }))} maxLength={MAX_NOTE} className="ns-input" placeholder="Fixed in the 14 October build" />
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      {group.actions.map(({ action, label, kind }) => (
                        <button key={action} type="button" disabled={busy === report.id} onClick={() => decide(report, action)} className={`ns-btn ns-btn-sm ${kind}`} data-bug-action={action}>
                          {busy === report.id ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : null}
                          {label}
                        </button>
                      ))}
                    </div>
                  </article>
                ))}
              </section>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default BugReviewPage;
